<template>
	<div :class="baseClasses" @click="OnClick" @mouseover="hover = true" @mouseleave="hover = false">
		<template v-if="!reactive">
			<o-skeleton v-if="loading" :width="imgWidthCss" :height="imgWidthCss"></o-skeleton>
			<div class="box" :class="imgClasses" v-if="!hasDataUrl || hasError">
				<span class="initials">{{ initials }}</span>
			</div>
			<img
				v-else
				:key="dataUrl"
				:class="[imgClasses, { 'img-pending': loading }]"
				:data-url="dataUrl"
				:src="dataUrl"
				@load="OnImgLoad"
				@error="OnImgError" />
		</template>
		<template v-else>
			<o-skeleton v-if="loading" :width="imgWidthCss" :height="imgWidthCss"></o-skeleton>
			<div class="box" :class="imgClasses" v-if="!hasDataUrl || (hasDataUrl && hasError)">
				<span class="initials">{{ initials }}</span>
				<div class="overlay" v-if="showNameAndCity">
					<span class="name">{{ firstName }}&nbsp;{{ lastName }}</span>
					<span class="city">
						<o-icon icon="map-marker-alt"></o-icon>
						{{ city }}
					</span>
				</div>
				<div class="details" v-if="showHoverDetails">
					<p class="name">{{ firstName }}&nbsp;{{ lastName }}</p>
					<p class="position">{{ title }}</p>
					<p class="city">
						<o-icon icon="map-marker-alt"></o-icon>
						{{ city }}
					</p>
					<p class="hr"></p>
					<p class="phone" v-if="officePhone">
						<o-icon icon="phone"></o-icon>
						Office:
						<span class="main-content" @click.stop="CallPhone">{{ officePhone }}</span>
					</p>
					<p class="email" v-if="email" @click.stop="MailTo">
						<o-icon icon="envelope"></o-icon>
						<span class="main-content" v-html="email"></span>
					</p>
					<p class="viewprofile">- View profile</p>
				</div>
			</div>

			<template v-else>
				<img
					:key="dataUrl"
					:class="[imgClasses, { 'img-pending': loading }]"
					:data-url="dataUrl"
					:src="dataUrl"
					@load="OnImgLoad"
					@error="OnImgError" />
				<div class="overlay" v-if="showNameAndCity">
					<p class="name">{{ firstName }}&nbsp;{{ lastName }}</p>
					<p class="city">
						<o-icon icon="map-marker-alt"></o-icon>
						{{ city }}
					</p>
				</div>
				<div class="details" v-if="showHoverDetails">
					<p class="name">{{ firstName }}&nbsp;{{ lastName }}</p>
					<p class="position">{{ title }}</p>
					<p class="city">
						<o-icon icon="map-marker-alt"></o-icon>
						{{ city }}
					</p>
					<p class="hr"></p>
					<p class="phone" v-if="officePhone">
						<o-icon icon="phone"></o-icon>
						Office:
						<span class="main-content" @click.stop="CallPhone">{{ officePhone }}</span>
					</p>
					<p class="email" v-if="email" @click.stop="MailTo">
						<o-icon icon="envelope"></o-icon>
						<span class="main-content" v-html="email"></span>
					</p>
					<p class="viewprofile">- View profile</p>
				</div>
			</template>
		</template>
	</div>
</template>
<script>
	import { ref, watch, computed } from "vue";
	import { useRouter } from "vue-router";
	import Axios from "axios";
	export default {
		props: {
			oid: {
				type: String,
				default: null
			},
			uuid: {
				type: String,
				default: null
			},
			user: {
				type: Object,
				default: () => {
					return {};
				}
			},
			size: {
				type: String,
				default: "medium"
			},
			reactive: {
				type: Boolean,
				default: false
			},
			link: {
				type: Boolean,
				default: false
			},
			square: {
				type: Boolean,
				default: false
			},
			field: {
				type: String,
				default: "TileImage"
			}
		},
		emits: ["click"],
		setup(props, context) {
			const $router = useRouter();

			const loading = ref(true);
			const hasError = ref(false);
			const hover = ref(false);

			const firstName = computed(() => {
				return props.user.firstName || props.user.FirstName;
			});
			const lastName = computed(() => {
				return props.user.lastName || props.user.LastName;
			});
			const user_oid = computed(() => {
				let obj = props.uuid || props.oid;
				if (!obj && props.user)
					obj =
						props.user.uuid ||
						props.user.UUID ||
						props.user.partnerUUID ||
						props.user.PartnerUUID ||
						props.user.UserUUID ||
						props.user.oid ||
						props.user.OID;

				return obj;
			});
			const initialImageField = computed(() => {
				if (props.user.UserUUID && !(props.user.partnerUUID || props.user.PartnerUUID)) {
					return "tileimage";
				}
				return props.field;
			});
			const currentImageField = ref(initialImageField.value);
			const city = computed(() => {
				return props.user.location || props.user?.Location?.Name;
			});
			const title = computed(() => {
				return props.user.title || props.user.Title;
			});
			const officePhone = computed(() => {
				return props.user.officePhone || props.user.OfficePhone;
			});
			const email = computed(() => {
				const e = props.user.email || props.user.Email;
				if (!e) return null;
				const parts = e.split("@");
				return parts[0] + "<wbr>@" + parts[1];
			});

			const imgWidthRem = computed(() => {
				switch (props.size) {
					case "nano":
						return 2.5;
					case "micro":
						return 5;
					case "small":
						return 8;
					case "medium":
						return 10;
					case "large":
						return 12;
					case "xlarge":
						return 14;
					case "xxlarge":
						return 18;
					case "huge":
						return 22;
				}
				return 10;
			});

			const imgWidthCss = computed(() => {
				return `${imgWidthRem.value}rem`;
			});

			const imgWidthPx = computed(() => {
				return (imgWidthRem.value * 16).toString();
			});

			const initials = computed(() => {
				if (props.oid || props.uuid) {
					return null;
				}

				if (firstName.value && lastName.value) {
					return (
						firstName.value.substring(0, 1).toUpperCase() +
						lastName.value.substring(0, 1).toUpperCase()
					);
				} else if (firstName.value) {
					return firstName.value.substring(0, 1).toUpperCase();
				} else if (lastName.value) {
					return lastName.value.substring(0, 1).toUpperCase();
				}

				return "";
			});

			const currentImageFieldOid = computed(() => {
				const fieldKey = (currentImageField.value || "").toLowerCase();
				const fieldOidMap = {
					image: "35914",
					metaimage: "2180626",
					squarephoto: "127327D",
					usersquarephoto: "43489847D",
					banner: "181981",
					tile: "385878",
					tileimage: "385878",
					tileimagefallback: "54344599"
				};
				return fieldOidMap[fieldKey] || "385878";
			});

			const dataUrl = computed(() => {

				return (
					(Axios.defaults.baseURL || "").replace(/\/$/, "") +
					"/api/v2/cms/image/view/" +
					encodeURIComponent(user_oid.value) +
					"/" +
					currentImageFieldOid.value +
					"?size=" +
					imgWidthPx.value +
					"x" +
					imgWidthPx.value
				);
			});

			const hasDataUrl = computed(() => {
				const val = user_oid.value;
				return val !== undefined && val !== null;
			});

			const baseClasses = computed(() => {
				return {
					"user-picture": true,
					"user-picture_reactive": props.reactive,
					"user-picture_static": !props.reactive,
					hover: hover.value,
					square: props.square
				};
			});

			const imgClasses = computed(() => {
				const obj = {
					square: props.square
				};
				if (props.size && props.size !== "expanded") {
					obj[props.size] = true;
				}
				return obj;
			});

			const showNameAndCity = computed(() => {
				return (
					props.reactive && ["medium", "large", "xlarge", "xxlarge", "huge"].includes(props.size)
				);
			});

			const showHoverDetails = computed(() => {
				return props.reactive && ["large", "xlarge", "xxlarge", "huge"].includes(props.size);
			});

			const IsCurrentImageEvent = (event) => {
				return event?.target?.dataset?.url === dataUrl.value;
			};

			const OnImgLoad = (event) => {
				if (!IsCurrentImageEvent(event)) return;
				loading.value = false;
			};

			const OnImgError = (event) => {
				if (!IsCurrentImageEvent(event)) return;
				if (currentImageFieldOid.value === "385878") {
					currentImageField.value = "tileimagefallback";
					return;
				}
				if (currentImageFieldOid.value === "54344599") {
					currentImageField.value = "squarephoto";
					return;
				}
				else if ((currentImageField.value || "").toLowerCase() === "squarephoto") {
					currentImageField.value = "usersquarephoto";
					return;
				}
				hasError.value = true;
				loading.value = false;
			};

			const OnClick = () => {
				if (props.link) {
					$router.push({ name: "_partnerProfile", params: { Id: props.user.ID || props.user.Id } });
				} else {
					context.emit("click");
				}
			};

			const MailTo = () => {
				const a = document.createElement("a");
				a.href = "mailto:" + (props.user.email || props.user.Email);
				a.click();
			};

			const CallPhone = () => {
				const a = document.createElement("a");
				a.href = "tel:" + (props.user.officePhone || props.user.OfficePhone);
				a.click();
			};

			watch(
				[initialImageField, user_oid, imgWidthPx],
				([value]) => {
					currentImageField.value = value;
				},
				{ immediate: true }
			);

			watch(
				() => dataUrl.value,
				() => {
					hasError.value = false;
					loading.value = hasDataUrl.value;
				},
				{ immediate: true }
			);

			watch(
				() => hasDataUrl.value,
				(value) => {
					if (!value) {
						hasError.value = false;
						loading.value = false;
					}
				}
			);

			return {
				baseClasses,
				imgClasses,
				loading,
				hasError,
				dataUrl,
				hasDataUrl,
				firstName,
				lastName,
				user_oid,
				city,
				initials,
				title,
				officePhone,
				email,
				imgWidthCss,
				hover,
				showNameAndCity,
				showHoverDetails,
				OnImgLoad,
				OnImgError,
				OnClick,
				MailTo,
				CallPhone
			};
		}
	};
</script>
<style lang="scss" scoped>
	@use "@/assets/css/globalVars.scss" as *;

	.picture {
		position: relative;
	}

	.user-picture {
		position: relative;
		line-height: 0;

		& > * {
			line-height: normal;
		}

		& p {
			color: #fff;

			&.name {
				font-weight: 600;
			}
		}

		& span.initials {
			margin: 0;
			font-weight: 700;
			color: $text-light-gray;
			pointer-events: none;
			user-select: none;
		}

		&.user-picture_reactive {
			& span.initials {
				margin: 0;
				margin-bottom: 20%;
				color: $boyden-blue-light-tint;
			}

			& .box {
				background-color: $boyden-blue-lighter;
				color: $boyden-blue-light;
			}
		}

		& img {
			display: block;
			border-radius: $radius-medium;
			aspect-ratio: 1 / 1;
			object-fit: cover;

			&.img-pending {
				visibility: hidden;
			}

			&.square {
				border-radius: 0;
			}
			&.nano,
			&.nano + .overlay {
				width: 2.5rem;
				min-width: 2.5rem;
				font-size: 0.33rem;
				border-radius: $radius-small;
			}
			&.micro,
			&.micro + .overlay {
				width: 5rem;
				min-width: 5rem;
				font-size: 0.33rem;
			}
			&.small,
			&.small + .overlay {
				width: 8rem;
				min-width: 8rem;
				font-size: 0.571rem;
			}

			&.medium,
			&.medium + .overlay {
				width: 10rem;
				min-width: 10rem;
				font-size: 0.714rem;
			}

			&.large,
			&.large + .overlay {
				width: 12rem;
				min-width: 12rem;
				font-size: 0.857rem;
			}

			&.xlarge,
			&.xlarge + .overlay {
				width: 14rem;
				min-width: 14rem;
				font-size: 1rem;
			}

			&.xxlarge,
			&.xxlarge + .overlay {
				width: 18rem;
				min-width: 18rem;
				font-size: 1.285rem;
			}

			&.huge,
			&.huge + .overlay {
				width: 22rem;
				min-width: 22rem;
				font-size: 1.285rem;
			}
		}

		& .overlay {
			align-items: center;
			justify-content: center;
			opacity: 1;
			border-radius: 0 0 $radius-medium $radius-medium;
			height: 4em !important;
			background-color: rgba(#00aeef, 0.8);
			font-size: 1em;
			text-align: center;
			color: #fff;

			& > .city {
				font-size: 0.8em;
				font-weight: 200;
				color: #fff;

				& > .icon {
					position: relative;
					top: 0.1em;
					margin-right: 0.2em;
					font-size: 1.2em;
					width: 1em;
				}
			}
		}

		& .details {
			padding: 1.25em 0.938em;
			align-items: flex-start;
			justify-content: space-between;
			opacity: 0;
			border-radius: $radius-medium;
			width: 100%;
			height: 100%;

			background-color: #00aeef;
			color: #fff;

			font-size: 0.9em;

			& .icon {
				height: 1.2em;
				width: 1em;
				margin-right: 0.6em;
				font-size: 1em;
			}

			& > .city {
				font-size: 0.9em;
				font-weight: 200;

				& > .icon {
					//position: relative;
					//top: 0.2em;
					//margin-right: 0.2em;
					//font-size: 1.2em;
					//width: 1em;
				}
			}

			& .name {
				font-weight: 600;
			}

			& .position {
				font-size: 0.9em;
				font-weight: 500;
			}

			& .email {
				// display: flex;
				// flex-direction: row;
				// align-items: flex-start;
			}

			& .phone,
			& .email {
				//margin-left: -0.2em;
				white-space: nowrap;
				font-size: 0.9em;
			}

			& .hr {
				display: block;
				margin: 1em 0;
				width: 100%;
				height: 0;
				border-bottom: 1px solid rgba(255, 255, 255, 0.2);
			}

			& .viewprofile {
				//margin-top: auto;
				font-size: 0.9em;
			}
		}

		& .overlay,
		& .details {
			position: absolute;
			bottom: 0;
			display: flex;
			flex-direction: column;

			box-sizing: border-box;

			transition: opacity 0.25s ease-out;

			font-weight: 700;
			letter-spacing: 0.05em;
			color: #fff;
		}

		& .box {
			line-height: normal;
			box-shadow: none;
			border-radius: 4px;
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			border: none;
			background-color: $color-background;

			&.square {
				border-radius: 0;
			}

			&.nano,
			&.nano + .overlay {
				background-color: #777;
				width: 2.5rem;
				min-width: 2.5rem;
				height: 2.5rem;
				padding: 0;
				font-size: 0.35rem;
				background-color: $text-light-gray;
				& span.initials {
					font-weight: 400;
					color: #fff;
				}
			}

			&.micro,
			&.micro > .overlay {
				width: 5rem;
				min-width: 5rem;
				height: 5rem;
				font-size: 0.7rem;
				background-color: $text-light-gray;
				& span.initials {
					font-weight: 500;
					color: #fff;
				}
			}
			&.small,
			&.small > .overlay {
				width: 8rem;
				min-width: 8rem;
				height: 8rem;
				font-size: 0.571rem;
			}
			&.medium,
			&.medium > .overlay {
				width: 10rem;
				min-width: 10rem;
				height: 10rem;
				font-size: 0.714rem;
			}
			&.large,
			&.large > .overlay {
				width: 12rem;
				min-width: 12rem;
				height: 12rem;
				font-size: 0.8571rem;
			}
			&.xlarge,
			&.xlarge > .overlay {
				width: 14rem;
				min-width: 14rem;
				height: 14rem;
				font-size: 1rem;
			}
			&.xxlarge,
			&.xxlarge + .overlay {
				width: 18rem;
				min-width: 18rem;
				height: 18rem;
				font-size: 1.285rem;
			}
			&.huge,
			&.huge + .overlay {
				width: 22rem;
				min-width: 22rem;
				height: 22rem;
				font-size: 1.285rem;
			}
			& > .initials {
				font-size: 3em;
			}
		}

		// & > .interactive-picture {
		// 	display: inline-block;
		// 	cursor: pointer;

		// 	&:hover img + .overlay {
		// 		opacity: 0.8;
		// 	}

		// 	& .parent {
		// 		position: relative;

		// 		& > img {
		// 			& + .overlay {
		// 				position: absolute;
		// 				bottom: 5px;
		// 				text-align: center;
		// 				background-color: #5dccff;
		// 				transition: 0.5s opacity linear;
		// 			}
		// 		}
		// 	}
		// }

		&.user-picture_reactive.hover {
			//filter: grayscale(100%);
			cursor: pointer;

			& .details {
				opacity: 1;
			}
		}

		p.email:hover {
			& > .main-content {
				text-decoration: underline;
			}
		}
		p.phone {
			& > .main-content:hover {
				text-decoration: underline;
			}
		}
	}
</style>
